const RAW_CENTRAL_AUTH_ORIGIN = import.meta.env.VITE_CENTRAL_AUTH_ORIGIN as string | undefined;
// Must be an absolute http(s) origin. Reject empty/"undefined"/"null"/relative so
// a misconfigured build can never yield a relative "undefined/login" redirect that
// the browser accumulates into an infinite /undefined/.../login loop.
export const CENTRAL_AUTH_ORIGIN =
  RAW_CENTRAL_AUTH_ORIGIN && /^https?:\/\//i.test(RAW_CENTRAL_AUTH_ORIGIN)
    ? RAW_CENTRAL_AUTH_ORIGIN.replace(/\/+$/, '')
    : 'https://apps.surbhi.net';
const POST_LOGIN_REDIRECT_KEY = 'spm_post_login_redirect';
const POST_LOGIN_REDIRECT_SESSION_KEY = 'spm_post_login_redirect_session';
const MSAL_SSO_STATE_PREFIX = 'spm-sso:';

function safeParseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getCentralAuthHost(): string {
  const parsed = safeParseUrl(CENTRAL_AUTH_ORIGIN);
  return parsed?.hostname || 'apps.surbhi.net';
}

export function isSurbhiHostname(hostname: string): boolean {
  return hostname === 'surbhi.net' || hostname.endsWith('.surbhi.net');
}

export function isCentralAuthHostname(hostname: string): boolean {
  const centralHost = getCentralAuthHost();
  // apps.surbhi.net, emd.surbhi.net, letter.surbhi.net, and activity.surbhi.net handle authentication directly
  return (
    hostname === centralHost ||
    hostname === 'emd.surbhi.net' ||
    hostname === 'letter.surbhi.net' ||
    hostname === 'letter.surbhi.net' ||
    hostname === 'activity.surbhi.net'
  );
}

export function isStandaloneSurbhiApp(hostname: string): boolean {
  return isSurbhiHostname(hostname) && !isCentralAuthHostname(hostname);
}

export function isAllowedReturnUrl(candidate: string): boolean {
  const parsed = safeParseUrl(candidate);
  if (!parsed) return false;

  const isHttpScheme = parsed.protocol === 'https:' || parsed.protocol === 'http:';
  if (!isHttpScheme) return false;

  // Allow HTTPS Surbhi domains everywhere.
  if (parsed.protocol === 'https:' && isSurbhiHostname(parsed.hostname)) return true;

  // Allow localhost ONLY when this app is itself running locally (dev). On the
  // deployed demo/production host we must never send users to an internal URL.
  const selfHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const selfIsLocal = selfHost === 'localhost' || selfHost === '127.0.0.1';
  if (selfIsLocal && (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'))
    return true;

  return false;
}

export function getSafeNextFromSearch(search: string): string | null {
  const next = new URLSearchParams(search).get('next') || '';
  if (!next) return null;
  return isAllowedReturnUrl(next) ? next : null;
}

export function buildCentralLoginUrl(nextUrl: string): string {
  const safeNext = isAllowedReturnUrl(nextUrl) ? nextUrl : CENTRAL_AUTH_ORIGIN;
  return `${CENTRAL_AUTH_ORIGIN}/login?next=${encodeURIComponent(safeNext)}`;
}

export function buildCentralLogoutUrl(): string {
  return `${CENTRAL_AUTH_ORIGIN}/login?logged_out=1`;
}

export function persistPostLoginRedirect(nextUrl: string): void {
  if (!isAllowedReturnUrl(nextUrl)) return;
  localStorage.setItem(POST_LOGIN_REDIRECT_KEY, nextUrl);
  sessionStorage.setItem(POST_LOGIN_REDIRECT_SESSION_KEY, nextUrl);
}

export function consumePostLoginRedirect(): string | null {
  const localValue = localStorage.getItem(POST_LOGIN_REDIRECT_KEY) || '';
  const sessionValue = sessionStorage.getItem(POST_LOGIN_REDIRECT_SESSION_KEY) || '';
  localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_SESSION_KEY);

  if (localValue && isAllowedReturnUrl(localValue)) return localValue;
  if (sessionValue && isAllowedReturnUrl(sessionValue)) return sessionValue;
  return null;
}

export function buildMsalSsoState(nextUrl: string): string {
  const payload = JSON.stringify({ next: nextUrl, ts: Date.now() });
  return `${MSAL_SSO_STATE_PREFIX}${btoa(payload)}`;
}

export function extractNextFromMsalState(state?: string | null): string | null {
  if (!state || !state.startsWith(MSAL_SSO_STATE_PREFIX)) return null;

  try {
    const encoded = state.slice(MSAL_SSO_STATE_PREFIX.length);
    const parsed = JSON.parse(atob(encoded)) as { next?: string };
    const next = parsed?.next || '';
    return isAllowedReturnUrl(next) ? next : null;
  } catch {
    return null;
  }
}
